<template>
    <div>
        <Popover>
            <PopoverTrigger as-child>
                <DockIconButton aria-label="Settings" title="Settings">
                    <Settings class="size-icon-lg" aria-hidden="true" />
                </DockIconButton>
            </PopoverTrigger>
            <PopoverContent :side="popoverSide" align="end" class="w-72">
                <!-- Suppress the Dashboard CTA on test-only variants
                     (DPI is a public-facing test surface where
                     admin/dashboard access doesn't apply). -->
                <template v-if="!isTestOnly">
                    <Button
                        variant="ghost"
                        class="w-full justify-start gap-2"
                        @click="goToDashboard"
                    >
                        <LayoutDashboard class="size-icon-md" />
                        <span class="text-prose">Dashboard</span>
                    </Button>
                    <Separator class="my-3" />
                </template>
                <div class="flex items-center justify-between">
                    <span class="section-label text-body">Theme</span>
                    <DarkModeToggle size="sm" />
                </div>
                <!-- Popover-section async-resolution fades. The IP and
                     entity sections resolve async; `fade-slide` enters
                     from `translateY(-4px)` + opacity to match the
                     popover's own entrance rhythm. -->
                <Transition name="fade-slide">
                    <Separator v-if="resolvedClientIp" class="my-3" />
                </Transition>
                <Transition name="fade-slide">
                    <section v-if="resolvedClientIp">
                        <h4 class="section-label text-body">Connection</h4>
                        <ScrollingText :text="resolvedClientIp" class="mt-1 block font-mono text-prose" />
                        <ScrollingText
                            v-if="resolvedIpInfo?.org"
                            :text="resolvedIpInfo.org"
                            class="mt-0.5 block text-prose italic text-muted-foreground"
                        />
                    </section>
                </Transition>
                <Transition name="fade-slide">
                    <Separator v-if="resolvedLookedUpIp?.row && resolvedClientIp" class="my-3" />
                </Transition>
                <Transition name="fade-slide">
                    <section v-if="resolvedLookedUpIp?.row">
                        <h4 class="section-label text-body">Entity</h4>
                        <ScrollingText
                            :text="resolvedLookedUpIp.row['Entity Name']"
                            class="mt-1 block text-prose text-th-accent"
                        />
                        <ScrollingText
                            :text="resolvedLookedUpIp.row['Entity ID']"
                            class="mt-0.5 block font-mono text-body text-muted-foreground"
                        />
                    </section>
                </Transition>
                <!-- Dev configurator (gated by ?dev=1 URL param) -->
                <template v-if="showDevConfig">
                    <Separator class="my-3" />
                    <section>
                        <h4 class="section-label text-body">Test Config</h4>
                        <div class="mt-2 grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1.5">
                            <label class="text-body text-muted-foreground">Download (s)</label>
                            <Input type="number" :model-value="devConfig.downloadDuration / 1000" @update:model-value="v => updateConfig('downloadDuration', toNumber(v) * 1000)" :min="1" :max="60" class="h-7 text-prose" />
                            <label class="text-body text-muted-foreground">Upload (s)</label>
                            <Input type="number" :model-value="devConfig.uploadDuration / 1000" @update:model-value="v => updateConfig('uploadDuration', toNumber(v) * 1000)" :min="1" :max="60" class="h-7 text-prose" />
                            <label class="text-body text-muted-foreground">Streams</label>
                            <Input type="number" :model-value="devConfig.downloadStreams" @update:model-value="v => updateConfig('downloadStreams', toNumber(v))" :min="1" :max="16" class="h-7 text-prose" />
                            <label class="text-body text-muted-foreground">Chunk (MB)</label>
                            <Input type="number" :model-value="devConfig.chunkSizeMB" @update:model-value="v => updateConfig('chunkSizeMB', toNumber(v))" :min="1" :max="500" class="h-7 text-prose" />
                            <label class="text-body text-muted-foreground">Pings</label>
                            <Input type="number" :model-value="devConfig.pingCount" @update:model-value="v => updateConfig('pingCount', toNumber(v))" :min="1" :max="50" class="h-7 text-prose" />
                        </div>
                    </section>
                </template>
            </PopoverContent>
        </Popover>
    </div>
</template>

<script setup lang="ts">
import { computed, reactive, inject } from "vue";
import { useRoute, useRouter } from "vue-router";
import LayoutDashboard from "@lucide/vue/dist/esm/icons/layout-dashboard.mjs";
import Settings from "@lucide/vue/dist/esm/icons/settings.mjs";
import {
    Popover, PopoverTrigger, PopoverContent,
} from "@mkbabb/glass-ui/popover";
import { Separator } from "@mkbabb/glass-ui/separator";
import { Button } from "@mkbabb/glass-ui/button";
import { Input } from "@mkbabb/glass-ui/forms";
import { ScrollingText } from "@mkbabb/glass-ui/scrolling-text";
import { DarkModeToggle } from "@mkbabb/glass-ui/controls";
import { DockIconButton } from "@mkbabb/glass-ui/dock";
import type { IPInfo, LookedUpIP } from "@src/types/dns";
import { IPInfoKey } from "@src/composables/keys.ts";
import { useSpeedtestStore } from "@features/speedtest";
import { useVariantStore } from "@src/stores/useVariantStore.ts";
import {
    DEFAULT_DOWNLOAD_DURATION_MS,
    DEFAULT_UPLOAD_DURATION_MS,
    DEFAULT_DOWNLOAD_STREAMS,
    DEFAULT_CHUNK_SIZE_MB,
    DEFAULT_PING_COUNT,
} from "@speedtest/constants.ts";

const props = withDefaults(
    defineProps<{
        clientIp?: string;
        ipInfo?: IPInfo | null;
        lookedUpIp?: LookedUpIP | null;
        /**
         * Popover open direction. The canonical mount-point is the bottom
         * dock (anchor at viewport bottom → opens up); dashboard layouts
         * pass `"bottom"` since they mount the trigger at the top of their
         * nav row.
         */
        popoverSide?: "top" | "right" | "bottom" | "left";
    }>(),
    {
        clientIp: "",
        ipInfo: null,
        lookedUpIp: null,
        popoverSide: "top",
    },
);

// Inject from App.vue when props aren't provided (e.g. on dashboard routes)
const injectedIpInfo = inject(IPInfoKey, null);

// Test-only variants (DPI) suppress the admin Dashboard CTA — DPI is
// a public test surface where dashboard access doesn't apply.
const variantStore = useVariantStore();
const isTestOnly = computed(() => variantStore.variant.surveyMode === "none");

const resolvedClientIp = computed(() =>
    props.clientIp || injectedIpInfo?.clientIp?.value || "",
);
const resolvedIpInfo = computed(() =>
    props.ipInfo ?? injectedIpInfo?.ipInfo?.value ?? null,
);
const resolvedLookedUpIp = computed(() =>
    props.lookedUpIp ?? injectedIpInfo?.lookedUpIp?.value ?? null,
);

// ── Dev configurator (gated by ?dev=1) ───────────────────────────────

const route = useRoute();
const router = useRouter();
const showDevConfig = computed(() => route.query.dev === "1");

function goToDashboard() {
    router.push({ name: "admin" });
}

const devConfig = reactive({
    downloadDuration: DEFAULT_DOWNLOAD_DURATION_MS,
    uploadDuration: DEFAULT_UPLOAD_DURATION_MS,
    downloadStreams: DEFAULT_DOWNLOAD_STREAMS,
    chunkSizeMB: DEFAULT_CHUNK_SIZE_MB,
    pingCount: DEFAULT_PING_COUNT,
});

type DevConfigKey = keyof typeof devConfig;

const speedtest = useSpeedtestStore();

function toNumber(value: string | number): number {
    return Number(value);
}

function updateConfig(key: DevConfigKey, value: number) {
    devConfig[key] = value;
    speedtest.configure({ [key]: value });
}
</script>
